import { VAULT_NAV_STORAGE_KEY } from './vault-session-storage.util';

import { VaultAppToastService, type VaultAppToastEvent } from './vault-app-toast.service';

export const VAULT_TABS_SNAPSHOT_KEY = 'vault_tabs_snapshot_v1';

export interface VaultTabsSnapshotTab {
  id: string;
  title: string;
  route: string;
  icon?: string;
}

export interface VaultTabsSnapshot {
  tabs: VaultTabsSnapshotTab[];
  activeId?: string;
}

function decodeNebulaNavStorage(raw: string): unknown | null {
  try {
    return JSON.parse(atob(raw));
  } catch {
    return null;
  }
}

/** Lee pestañas del contexto `layout` en `nebula_ui_nav_v1`. */
export function readVaultLayoutTabsFromNavStorage(): VaultTabsSnapshot | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(VAULT_NAV_STORAGE_KEY)?.trim();
  if (!raw) return null;

  const data = decodeNebulaNavStorage(raw) as {
    sessions?: Array<[string, { tabs?: unknown[]; activeId?: unknown }]>;
  } | null;
  if (!data?.sessions?.length) return null;

  const layoutEntry = data.sessions.find(([contextId]) => contextId === 'layout');
  if (!layoutEntry) return null;

  const state = layoutEntry[1];
  const tabsRaw = Array.isArray(state.tabs) ? state.tabs : [];
  const tabs: VaultTabsSnapshotTab[] = tabsRaw
    .map((tab) => {
      if (!tab || typeof tab !== 'object') return null;
      const t = tab as Record<string, unknown>;
      const route = String(t['route'] ?? '').trim();
      if (!route) return null;
      const id = String(t['id'] ?? route).trim();
      const parsed: VaultTabsSnapshotTab = {
        id,
        title: String(t['title'] ?? '').trim() || route,
        route,
      };
      if (t['icon'] != null) {
        parsed.icon = String(t['icon']);
      }
      return parsed;
    })
    .filter((tab): tab is VaultTabsSnapshotTab => tab != null);

  if (!tabs.length) return null;

  const activeId =
    state.activeId != null && String(state.activeId).trim()
      ? String(state.activeId).trim()
      : undefined;

  return { tabs, activeId };
}

/** Guarda pestañas abiertas antes de un cierre automático de sesión (#0000004). */
export function captureVaultTabsSnapshotForExpiryLogout(): void {
  if (typeof window === 'undefined') return;
  const snapshot = readVaultLayoutTabsFromNavStorage();
  if (!snapshot?.tabs.length) return;
  sessionStorage.setItem(VAULT_TABS_SNAPSHOT_KEY, JSON.stringify(snapshot));
}

export function readVaultTabsSnapshotForExpiryLogin(): VaultTabsSnapshot | null {
  if (typeof window === 'undefined') return null;
  const raw = sessionStorage.getItem(VAULT_TABS_SNAPSHOT_KEY)?.trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as VaultTabsSnapshot;
    if (!Array.isArray(parsed?.tabs) || !parsed.tabs.length) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function consumeVaultTabsSnapshotForExpiryLogin(): VaultTabsSnapshot | null {
  const snapshot = readVaultTabsSnapshotForExpiryLogin();
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem(VAULT_TABS_SNAPSHOT_KEY);
  }
  return snapshot;
}

export function clearVaultTabsSnapshotSession(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(VAULT_TABS_SNAPSHOT_KEY);
}

/** Toast UI kit tras re-login por cierre automático (#0000004). */
export const VAULT_SESSION_RESTORED_TOAST: VaultAppToastEvent = {
  type: 'info',
  title: 'Sesión restaurada',
  message: 'Continuamos donde lo dejaste antes de que expirara la sesión.',
  duration: 5000,
};

export function notifyVaultSessionRestored(toast: VaultAppToastService): void {
  toast.show(VAULT_SESSION_RESTORED_TOAST);
}
